<template>
  <div class="correlationmap-wrapper">
    <el-row type="flex" class="treeButton" justify="space-between">
      <div class="vertical-center-box">
        <div class="setting-addel mr-r4">
          <el-button size="mini" @click="onZoom('zoomIn')"><i class="far fa-search-plus" style="font-size:14px;"></i></el-button>
          <el-button size="mini" @click="onZoom('zoomOut')"><i class="far fa-search-minus" style="font-size:14px;"></i></el-button>
        </div>
        <el-button class="vertical-center-box" size="mini" @click="onZoom('zoomInit')"><i class="far fa-sync-alt"></i></el-button>
      </div>
      <div class="vertical-center-box">
        <el-input size="mini" v-model="searchKeyword" clearable placeholder="검색 키워드를 입력해주십시오." style="width: 210px" @keyup.enter.native="search" @clear="search">
          <i class="el-input__icon el-icon-search" slot="suffix" style="cursor: pointer" @click="search"></i>
        </el-input>
      </div>
    </el-row>
    <div v-loading="loading" style="height:100%; width:100%;">
      <svg ref="svg" class="correlationmap-svg" width="100%" height="100%" />
      <div class="correlationmap-tooltip">
        <div style="color: blue">{{ tooltip.label }}</div>
        <div>{{ tooltip.rscInfo }}</div>
        <div>{{ tooltip.eventMessage }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { isNil, cloneDeep, isEmpty, compact } from 'lodash';
import msgUtil from '@/utils/message';
import { EMPTY_TEXT } from '@/utils/string';
import smartAnalysisApi from '@/api/common/smart-analysis';

export default {
  name: 'WEventCorrelationMap',
  props: {
    eventlevelInfo: {
      type: Object,
      default: () => ({}),
    },
    rcaInfo: {
      type: Object,
      default: () => ({}),
    },
    /**
    * 선택한 이벤트 패턴 hashkeyId 배열
    */
    hashkeyIds: {
      type: Array,
      default: () => ([]),
    },
  },
  data() {
    return {
      loading: false,
      nodes: [],
      links: [],
      markers: {},
      textElements: {},
      graphLayout: {},
      rcaMapData: [],
      adjlist: [],
      zoomHandler: null,
      zoom: {
        x: 0,
        y: 0,
        k: 1,
      },
      width: 0,
      height: 0,
      tooltip: { label: '', rscInfo: '', eventMessage: '' },
      searchKeyword: '',
      patternHashkeyIds: [],
      selectedNode: null,
    };
  },
  watch: {
    hashkeyIds(ids) {
      this.patternHashkeyIds = ids;
    },
    patternHashkeyIds() {
      // 선택한 이벤트 패턴 변경시, 검색 키워드가 존재하는지 우선적으로 체크
      this.search();
    },
  },
  methods: {
    /**
     * 줌 이벤트 핸들러
     * @author jeee12
     * @param  {String} zoomType 확대/축소/새로고침
     */
    onZoom(zoomType) {
      const svg = d3.select('.correlationmap-svg');

      if (zoomType === 'zoomIn') {
        svg.transition().call(this.zoomHandler.scaleBy, 1.2);
      } else if (zoomType === 'zoomOut') {
        svg.transition().call(this.zoomHandler.scaleBy, 0.8);
      } else if (zoomType === 'zoomInit') {
        if (!isNil(this.zoomHandler)) {
          this.zoomHandler.transform(svg, d3.zoomIdentity);
        }
        svg.selectAll('*').remove();
        this.loadData();
      }
    },
    /**
     * 맵 초기화
     * @author jeee12
     */
    initMap() {
      this.rcaMapData = [];
      this.searchKeyword = '';
      this.zoom = {
        x: 0,
        y: 0,
        k: 1,
      };
      if (!isNil(this.g)) {
        this.zoomHandler = d3.zoom().scaleExtent([0.5, 4]).on('zoom', () => {
          this.g.attr('transform', `translate(${this.zoom.x},${this.zoom.y})scale(${this.zoom.k})`);
        });
        this.zoomHandler.transform(d3.select('.correlationmap-svg'), d3.zoomIdentity);
      }
      this.selectedNode = cloneDeep(this.rcaInfo);
      this.removeMap();
      this.loadData();
    },
    /**
     * svg 영역에서 맵 삭제
     * @author jeee12
     * @return {[type]} [description]
     */
    removeMap() {
      const svg = d3.select('.correlationmap-svg');
      svg.selectAll('*').remove();
    },
    /**
     * 장애 근원지 이벤트 분석 상관관계 맵 데이터 조회
     * @author jeee12
     */
    loadData() {
      this.loading = true;
      const hashkeyIdString = this.rcaInfo.hashkeyIdString;
      const eventlevelId = this.rcaInfo.eventlevelId;
      smartAnalysisApi.getRcaEventCorrelationMapData({ hashkeyIdString, eventlevelId }).then((response) => {
        if (response.data.success) {
          this.rcaMapData = response.data.data;
          this.updateMap({ nodes: this.rcaMapData.nodeList, links: this.rcaMapData.linkList });
        } else {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 상관관계 맵', false));
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '이벤트 상관관계 맵', false));
        throw new Error(e);
      }).finally(() => {
        this.loading = false;
      });
    },
    /**
     * 이벤트 상관관계 맵 생성
     * @author jeee12
     * @param  {[type]} mapData node, link 배열
     */
    updateMap(mapData) {
      const svg = d3.select('.correlationmap-svg');
      const svgWidth = svg.style('width');
      const svgHeight = svg.style('height');
      let width = Number(svgWidth.substring(0, svgWidth.length - 2));
      let height = Number(svgHeight.substring(0, svgHeight.length - 2));
      if (width <= 10 && height <= 10) {
        width = window.innerWidth - (window.innerWidth / 5);
        height = window.innerHeight - (window.innerHeight / 5);
      }
      this.width = width;
      this.height = height;

      const graph = cloneDeep(mapData);
      const container = svg.append('g').attr('transform', `translate(${this.zoom.x},${this.zoom.y})scale(${this.zoom.k})`);

      // layout 생성
      this.graphLayout = d3.forceSimulation(graph.nodes)
        .force('charge', d3.forceManyBody().strength(-3000))
        .force('center', d3.forceCenter(width / 2, height / 2))
        .force('x', d3.forceX(width / 2).strength(1))
        .force('y', d3.forceY(height / 2).strength(1))
        .force('link', d3.forceLink(graph.links).id((d) => d.hashkeyIdString).distance(100).strength(1))
        .on('tick', () => {
          this.textElements
            .attr('x', (node) => node.x)
            .attr('y', (node) => node.y);
          this.nodes.call((node) => {
            node.attr('transform', (d) => `translate(${this.fixna(d.x)},${this.fixna(d.y)})`);
          });
          this.links.call((link) => {
            link.attr('x1', (d) => this.fixna(d.source.x))
              .attr('y1', (d) => this.fixna(d.source.y))
              .attr('x2', (d) => this.fixna(d.target.x))
              .attr('y2', (d) => this.fixna(d.target.y));
          });
        });

      // 화살표 속성 정의
      this.markers = container.append('g').attr('class', 'correlationmap-markers').append('defs')
        .selectAll('marker')
        .data(graph.links)
        .enter()
        .append('marker')
        .attr('id', (d) => `correlationmap-arrowhead${d.target.index}`)
        .attr('viewBox', '-0 -5 10 10')
        .attr('refX', (d) => (d.target.associationCount ? (d.target.associationCount * 2) + 14.5 : 14.5)) // node와 화살표 간의 거리
        .attr('refY', 0)
        .attr('orient', 'auto')
        .attr('markerWidth', 10) // 화살표 마커 너비
        .attr('markerHeight', 10) // 화살표 마커 높이
        .attr('xoverflow', 'visible')
        .append('svg:path')
        .attr('d', 'M 0,-5 L 10 ,0 L 0,5')
        .attr('fill', '#aaa')
        .style('stroke', 'none');

      // 노드 연결 링크 생성
      this.links = container.append('g').attr('class', 'correlationmap-links')
        .selectAll('line')
        .data(graph.links)
        .enter()
        .append('line')
        .attr('stroke', '#aaa')
        .attr('stroke-width', '1px')
        .attr('marker-end', (d) => `url(#correlationmap-arrowhead${d.target.index})`); // 노드 크기에 따른 링크 marker

      this.links.append('title')
        .text((d) => d.target.associationCount);

      // 노드 데이터에 설정한 사이즈에 따른 원의 크기 조정 (노드 생성)
      this.nodes = container.append('g').attr('class', 'correlationmap-nodes')
        .selectAll('g')
        .data(graph.nodes)
        .enter()
        .append('circle')
        .attr('class', 'circleItem c-pointer')
        .attr('r', (d) => (d.associationCount ? (d.associationCount * 2) + 5 : 5))
        .attr('fill', (d) => this.eventlevelInfo[d.eventlevelId].eventlevelColorBg);

      this.nodes.append('title')
        .text((d) => d.objName);

      this.nodes.append('text')
        .attr('dy', -3)
        .text((d) => `${d.objName}:${d.eventMessage}`);

      this.textElements = container.append('g').attr('class', 'correlationmap-text').selectAll('text')
        .data(graph.nodes)
        .enter()
        .append('text')
        .text((node) => `[${node.objName}] ${node.eventMessage}`)
        .attr('font-size', 11)
        .attr('dx', 15)
        .attr('dy', 4);

      this.adjlist = []; // 링크로 연결된 노드들의 연관 관계 저장
      graph.links.forEach((d) => {
        this.adjlist[`${d.source.index}-${d.target.index}`] = true;
        this.adjlist[`${d.target.index}-${d.source.index}`] = true;
      });

      this.zoomHandler = d3.zoom()
        .scaleExtent([0.1, 4])
        .on('zoom', () => { container.attr('transform', d3.event.transform); });
      svg.call(this.zoomHandler);

      this.nodes.call(
        d3.drag()
          .on('start', (d) => {
            d3.event.sourceEvent.stopPropagation();
            if (!d3.event.active) this.graphLayout.alphaTarget(0.3).restart();
            d.fx = d.x;
            d.fy = d.y;
          })
          .on('drag', (d) => {
            d.fx = d3.event.x;
            d.fy = d3.event.y;
          })
          .on('end', (d) => {
            if (!d3.event.active) this.graphLayout.alphaTarget(0);
            d.fx = null;
            d.fy = null;
          }),
      );

      // 클릭시 선택한 노드에 stroke 설정
      this.nodes.on('click', () => this.setSelectedNode(d3.select(d3.event.target)));
      // 선택한 이벤트 패턴이 존재할 경우, 포함된 노드-링크들만 표현
      this.highlightEventPattern(this.patternHashkeyIds);
      if (!isEmpty(this.selectedNode)) {
        const selectedItem = this.nodes.filter((d) => d.hashkeyIdString === this.selectedNode.hashkeyIdString);
        this.setSelectedNode(selectedItem);
      }
    },
    /**
     * 선택한 노드 강조 표현
     * @author jeee12
     * @param  {[type]}  selectItem 선택한 노드
     */
    setSelectedNode(selectItem) {
      this.nodes.style('stroke-width', '1px');
      this.nodes.style('stroke-opacity', '0');
      this.nodes.style('stroke', 'white');
      const datum = selectItem.datum();
      selectItem.style('stroke', this.eventlevelInfo[datum.eventlevelId].eventlevelColorBg);
      selectItem.style('stroke-width', '10px');
      selectItem.style('stroke-opacity', '0.5');
      this.textElements.style('font-weight', (d) => (d.index === datum.index ? 'bold' : 'normal')); // 선택된 노드 라벨 bold 설정
      this.selectedNode = datum;
      this.patternHashkeyIds = [];
      this.$emit('node-click', datum, this.rcaMapData.nodeList);
    },
    /**
     * node mouseover
     * @author jeee12
     * @param  {[type]} node        전달할 node
     * @param  {[type]} tooltipDiv  tooltip 객체
     * @param  {[type]} showLinks   마우스 오버시 연결된 노드-링크 표현 동작여부
     */
    mouseoverNode(node, tooltipDiv, showLinks) {
      // svg 좌표 계산하는 부분
      const circle = document.getElementsByClassName('circleItem')[node.index];
      const coords = circle.getBoundingClientRect(); // 상대 좌표
      tooltipDiv.transition().duration(200).style('opacity', 0.9);
      tooltipDiv.style('left', `${coords.left - ((this.width / 10) - 50)}px`).style('top', `${coords.top - ((this.height / 5) + 30)}px`);
      this.tooltip = {
        label: `${node.objName} (${node.ipaddress || EMPTY_TEXT})`,
        rscInfo: `${node.managetypeName} > ${node.rsctypeName} > ${node.rscName} > ${node.rscKeyName}`,
        eventMessage: node.eventMessage,
      };
      if (showLinks) {
        const index = d3.select(d3.event.target).datum().index;
        this.nodes.style('opacity', (o) => (this.neigh(index, o.index) ? 1 : 0.1));
        this.textElements.attr('display', (o) => (this.neigh(index, o.index) ? 'block' : 'none'));
        this.links.style('opacity', (o) => (o.source.index === index || o.target.index === index ? 1 : 0.1));
      }
    },
    /**
     * node mouseout
     * @author jeee12
     * @param  {[type]} tooltipDiv tooltip 객체
     * @param  {[type]} showLinks  마우스 오버시 연결된 노드-링크 표현 동작여부
     */
    mouseoutNode(tooltipDiv, showLinks) {
      tooltipDiv.transition().duration(500).style('opacity', 0);
      if (showLinks) {
        this.textElements.attr('display', 'block');
        this.nodes.style('opacity', 1);
        this.links.style('opacity', 1);
      }
    },
    /**
     * 선택한 이벤트 패턴에 대한 노드-링크 표현
     * @author jeee12
     * @param  {[type]} hashkeyIds 이벤트 패턴 hashkeyId 배열
     */
    highlightEventPattern(hashkeyIds) {
      const tooltipDiv = d3.select('.correlationmap-tooltip').style('opacity', 0); // 툴팁 영역
      if (!isEmpty(hashkeyIds)) {
        this.nodes
          .style('opacity', (d) => (hashkeyIds.indexOf(d.hashkeyIdString) > -1 ? '1' : '0.1'))
          .on('mouseover', (d) => {
            if (hashkeyIds.indexOf(d.hashkeyIdString) > -1) {
              this.mouseoverNode(d, tooltipDiv, false);
            }
          }).on('mouseout', () => this.mouseoutNode(tooltipDiv, false));

        // 링크 표현을 위한 배열 가공
        const patternLinks = hashkeyIds.reduce((acc, data, index) => {
          if (index < hashkeyIds.length - 1) {
            acc.push({ source: data, target: hashkeyIds[index + 1] });
          }
          return acc;
        }, []);
        this.links.style('opacity', (d) => {
          const showFlag = patternLinks.findIndex((p) => p.source === d.source.hashkeyIdString && p.target === d.target.hashkeyIdString) > -1;
          return showFlag ? '1' : '0.1';
        });
        this.textElements.attr('display', (d) => (hashkeyIds.indexOf(d.hashkeyIdString) > -1 ? 'block' : 'none'));
      } else {
        this.nodes
          .style('opacity', 1)
          .on('mouseover', (d) => this.mouseoverNode(d, tooltipDiv, true))
          .on('mouseout', () => this.mouseoutNode(tooltipDiv, true));
        this.textElements.attr('display', 'block');
        this.links.style('opacity', 1);
      }
    },
    neigh(a, b) {
      return a === b || this.adjlist[`${a}-${b}`];
    },
    fixna(x) {
      if (isFinite(x)) return x;
      return 0;
    },
    /**
     * 맵의 노드 검색
     * @author jeee12
     */
    search() {
      if (!isEmpty(this.rcaMapData)) {
        const links = this.links;
        const nodes = this.nodes;
        const textElements = this.textElements;
        const searchKeyword = this.searchKeyword.toLowerCase();
        const tooltipDiv = d3.select('.correlationmap-tooltip').style('opacity', 0);

        if (isEmpty(searchKeyword) || searchKeyword === '') {
          this.highlightEventPattern(this.patternHashkeyIds);
        } else {
          nodes.each((d) => {
            d.showFlag = false;
          }).style('opacity', 0.1);
          links.style('opacity', 0.1);

          let index = 0;
          nodes.filter((d) => {
            const matchWords = compact([d.objName, d.ipaddress, d.managetypeName, d.rsctypeName, d.rscName, d.rscKeyName, d.eventMessage]).join('');
            const showFlag = matchWords.toLowerCase().indexOf(searchKeyword) > -1;
            if (showFlag) index += 1;
            d.showFlag = showFlag;
            return showFlag;
          }).style('opacity', 1);
          textElements.attr('display', (d) => (d.showFlag ? 'block' : 'none'));

          nodes.on('mouseover', (d) => {
            if (d.showFlag) {
              this.mouseoverNode(d, tooltipDiv, false);
            }
          }).on('mouseout', () => this.mouseoutNode(tooltipDiv, false));

          if (index < 1) {
            links.style('opacity', 1);
            nodes.style('opacity', 1)
              .on('mouseover', (d) => this.mouseoverNode(d, tooltipDiv, true))
              .on('mouseout', () => this.mouseoutNode(tooltipDiv, true));
            textElements.attr('display', 'block');
            this.$message(msgUtil.getCustomMsgData('검색 조건에 일치하는 노드가 없습니다.'));
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">
.correlationmap-wrapper {
  height: 100%;
  background: #FFF;
  .treeButton {
    width: 60%;
    position: absolute;
    top: 40px;
    left: 40px;
  }
  div.correlationmap-tooltip {
    position: absolute;
    padding: 10px;
    font: 12px sans-serif;
    background: #FFF;
    border: 1px solid #ccc;
    border-left-width: 7px;
    pointer-events: none;
  }
}
@media only screen and (min-width: 0) and (max-width: 1450px) {
  .treeSearchInput {
    padding-left: 4px;
    width: 140px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/popup/EventSmartAnalyzerPopup/popup/components/EventCorrelationMap.vue